<script setup lang="ts">
import { useI18n } from 'vue-i18n'
const backToTopIcon = 'https://resources1.interface003.com/web30/2026/07/20/7646995a-d8e7-42b2-a6a4-708d3d311133.png'

definePageMeta({
  name: 'exchange-detail',
  layout: 'default',
  hideDefaultTopNav: true,
})

import { formatRelativeTime } from '~/utils/appTime'
import {
  getWebFeedbackFlyingScreen,
  getWebFeedbackPageList,
  type WebFeedbackFlyingScreenItem,
  type WebFeedbackFlyingScreenResponse,
  type WebFeedbackPageLabel,
  type WebFeedbackPageListData,
  type WebFeedbackPageListItem,
} from '~/api/feedback'
import {
  getTraderDetail,
  getGuaranteePop,
  getTraderAssetStrengthChart,
  getLicenseDetail,
  getAliasResolve,
  type LicenseDetail,
  type TraderExchangeOverview,
  type TraderOverviewTag,
  type TraderRatingCard,
  type FeeRate,
  type TraderBasicInfo,
  type TraderBasicInfoContact,
  type TraderBasicInfoDisplayItem,
} from '~/server/detail'

type FeedbackImage = {
  src: string
  alt?: string
}

type FeedbackEntry = {
  id: string
  name: string
  avatar: string
  submittedAt: string
  tag: string
  content: string
  images: FeedbackImage[]
  categoryKey?: string
  categoryLabel?: string
  detailUrl?: string
  highlighted?: boolean
}

type FeedbackCategory = {
  key: string
  label: string
  count?: number
  value?: string | number
}

type FeedbackCategoryCacheEntry = {
  data: WebFeedbackPageListData
  visibleCount: number
  isPageEnd: boolean
  loadedPageIndex: number
}

type WebFeedbackImage = {
  list?: string
  detail?: string
  url?: string
  word?: string
}

type WebFeedbackUserInfo = {
  nickName?: string
  userId?: string
  publishTime?: string
  publishTimeMil?: string
  avatarAddress?: string
}

type WebFeedbackTag = {
  text?: string
  name?: string
  title?: string
}

type WebFeedbackCommentInfo = {
  commentGradeId?: string
  title?: string
  content?: string
  translateTitle?: string
  translateContent?: string
  gradeName?: string
  negativeType?: string
  country?: string
  publishTime?: string
  image?: WebFeedbackImage[] | null
  images?: WebFeedbackImage[] | null
  tags?: Array<WebFeedbackTag | string> | null
}

type WebFeedbackItem = {
  userInfo?: WebFeedbackUserInfo | null
  tokenGradeCommentInfo?: WebFeedbackCommentInfo | null
}

type WebTraderDetailFeedback = {
  title?: string
  list?: WebFeedbackItem[] | null
  total?: string | number
  tips?: string
  showType?: number
  isMore?: boolean
}

type RegulationDisclosureTrader = {
  traderCode?: string
  traderName?: string
  englishFullName?: string
  icon?: string
}

type RegulationDisclosureSourceTrader = {
  code?: string
  logo?: string
  showName?: string
  localShortName?: string
  englishShortName?: string
  englishFullName?: string
  EnglishFullName?: string
  shortName?: string
  icon?: string
}

type RegulationDisclosureItem = {
  disclosureId?: string
  disclosureAlias?: string
  disclosureType?: string
  disclosureTypeColor?: string
  content?: string
  countryCode?: string
  countryFlag?: string
  countryName?: string
  regulatorName?: string
  disclosureDate?: string
  traders?: RegulationDisclosureTrader[]
}

type RegulationDisclosureSourceItem = {
  code?: string
  alias?: string
  category?: string
  categoryColor?: string
  title?: string
  country?: string
  countryCode?: string
  CountryCode?: string
  flag?: string
  regTime?: string
  traders?: RegulationDisclosureSourceTrader[] | null
}

type LicenseTranslateItem = {
  code?: string
  text?: string
}

const route = useRoute()
const { t, locale: activeLocale, localePath } = useAppI18n()
const normalizeTextValue = (value: unknown) => {
  if (value === null || value === undefined) return ''
  return String(value).trim()
}

const getFirstRouteValue = (value: unknown) => {
  if (Array.isArray(value)) return normalizeTextValue(value[0])
  return normalizeTextValue(value)
}
const routeAlias = getFirstRouteValue(route.params.slug)
const { CountryCode } = useAppStore()

const normalizeApiText = (value: unknown) => (value === null || value === undefined ? '' : String(value).trim())
const normalizeNullableApiText = (value: unknown) => normalizeApiText(value) || null
const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value && typeof value === 'object' && !Array.isArray(value))
const formatFeedbackCount = (value: unknown) => {
  const count = Number(value)

  return Number.isFinite(count) && count > 999 ? '999+' : String(Number.isFinite(count) && count >= 0 ? count : 0)
}
const feedbackTextKeyMap: Record<string, string> = {
  all: '83794',
  All: '83794',
  用户反馈: '83846',
  'User Feedback': '83846',
  反馈: '82430',
  Feedback: '82430',
  'Platform Fraud': '84018',
  平台欺诈: '84018',
  'Account frozen': '84017',
  账户冻结: '84017',
}

const translateFeedbackText = (value: string) => {
  const key = feedbackTextKeyMap[value]

  return key ? t(key) : value
}
const normalizeFeedbackCount = (value: unknown) => {
  const count = Number(value)

  return Number.isFinite(count) && count >= 0 ? count : 0
}
const normalizeFeedbackSearchType = (value?: string | number) => {
  const searchType = Number(value)

  return Number.isFinite(searchType) && searchType >= 0 ? searchType : 0
}
const normalizeFeedbackPagePayload = (
  response:
    | WebFeedbackPageListData
    | { data?: WebFeedbackPageListData; result?: WebFeedbackPageListData }
    | null
    | undefined,
) => {
  if (!response || typeof response !== 'object') {
    return null
  }

  if ('data' in response && isRecord(response.data)) {
    return response.data
  }

  if ('result' in response && isRecord(response.result)) {
    return response.result
  }

  return response as WebFeedbackPageListData
}
const normalizeFeedbackFlyingScreenPayload = (
  response: WebFeedbackFlyingScreenResponse | null | undefined,
): WebFeedbackFlyingScreenItem[] => {
  if (Array.isArray(response)) {
    return response
  }

  return Array.isArray(response?.result) ? response.result : Array.isArray(response?.data) ? response.data : []
}
const ASSET_STRENGTH_CHART_LIST_SIZE = 20
const ASSET_STRENGTH_CHART_POINT_LIMIT = 7
const FEEDBACK_PAGE_SIZE = 10
const FEEDBACK_INITIAL_VISIBLE_COUNT = 10
const FEEDBACK_AUTO_PAGE_LIMIT = 3
const FEEDBACK_BULLET_ITEM_LIMIT = 30
const WRITE_FEEDBACK_FOOTER_GAP = 120
const DEFAULT_TIME_RANGE_TYPE = 5
/** 优先使用接口分页元数据判断末页，元数据缺失时才以短页作为兜底。 */
const isFeedbackPageEnd = (data: WebFeedbackPageListData | null, accumulatedCount: number) => {
  const pageIndex = normalizeFeedbackCount(data?.pageIndex) || 1
  const pageCount = normalizeFeedbackCount(data?.pageCount)
  const activeLabelTotal =
    data?.labels?.find((label) => normalizeFeedbackSearchType(label?.value) === activeFeedbackSearchType.value)
      ?.count ?? 0
  const total = Math.max(
    normalizeFeedbackCount(data?.total),
    normalizeFeedbackCount(data?.showTotal),
    normalizeFeedbackCount(activeLabelTotal),
  )

  if (pageCount > pageIndex || total > accumulatedCount) {
    return false
  }

  if (pageCount > 0 || total > 0) {
    return true
  }

  return (data?.list?.length ?? 0) === 0
}
const normalizeTimeRangeType = (timeRangeType: string | number) => {
  const normalizedType = Number(timeRangeType)

  return Number.isFinite(normalizedType) ? normalizedType : DEFAULT_TIME_RANGE_TYPE
}
/** 将详情接口的时间范围字段统一为资金卡片可消费的 label/value 结构。 */
const normalizeAssetStrengthTimeRanges = (timeRange: unknown) => {
  if (Array.isArray(timeRange)) {
    return timeRange
      .map((item) => {
        const option = item as { label?: unknown; value?: unknown }
        const value = normalizeApiText(option.value)

        return value
          ? {
              label: normalizeApiText(option.label) || value,
              value,
            }
          : null
      })
      .filter((option): option is { label: string; value: string } => option !== null)
  }

  const options = (timeRange as { options?: unknown } | null)?.options

  if (!Array.isArray(options)) {
    return []
  }

  return options.map((option) => {
    const value = normalizeApiText(option)

    return {
      label: value,
      value,
    }
  })
}
const getChartPointCount = (chart?: { points?: unknown[]; pointsVal?: unknown[] }) =>
  Math.max(chart?.points?.length ?? 0, chart?.pointsVal?.length ?? 0)
const hasDetailData = (value: unknown) => {
  if (value === null || value === undefined) {
    return false
  }

  if (Array.isArray(value)) {
    return value.length > 0
  }

  if (typeof value === 'object') {
    return Object.keys(value).length > 0
  }

  return Boolean(value)
}
const hasAssetReserveChartData = (assetReserve?: { chart?: { slices?: Array<{ name?: string; percent?: string }> } }) =>
  (assetReserve?.chart?.slices ?? []).some((slice) => {
    const percent = Number.parseFloat(normalizeApiText(slice.percent).replace(/[%+,]/g, ''))

    return Boolean(normalizeApiText(slice.name)) && Number.isFinite(percent) && percent > 0
  })
const hasFundFlowChartData = (chart?: { points?: unknown[]; pointsVal?: unknown[] }) => {
  const values = chart?.pointsVal?.length ? chart.pointsVal : (chart?.points ?? [])

  return values.some((value) => {
    const point = Number(normalizeApiText(value).replace(/,/g, ''))

    return Number.isFinite(point) && point !== 0
  })
}
const matomo = useMatomo()

/**
 * 获取交易所的slug
 */
const exchangeSlug = computed(() => getFirstRouteValue(route.params.slug))
// console.log('exchangeSlug::: ', exchangeSlug.value)

const exchangeDetailRootPath = computed(() =>
  localePath({
    name: 'exchange-detail',
    params: {
      slug: exchangeSlug.value,
    },
  }),
)
const isExchangeDetailRootRoute = computed(() => route.path === exchangeDetailRootPath.value)
const isExchangeLicenseRoute = computed(() => route.path.endsWith('/licenses'))
const hasInvalidExchangeSlug = computed(() => /[A-Z]/.test(exchangeSlug.value))

const throwExchangeNotFound = (): never => {
  throw createError({
    statusCode: 404,
    statusMessage: 'Page Not Found',
    fatal: true,
  })
}

const { data: aliasResolveData, error: aliasResolveError } = await useAsyncData(
  () => `alias-resolve-${exchangeSlug.value}`,
  async () => {
    if (hasInvalidExchangeSlug.value || !exchangeSlug.value) {
      throwExchangeNotFound()
    }

    return await getAliasResolve({ alias: exchangeSlug.value }).catch(() => null)
  },
)

if (aliasResolveError.value) {
  throw aliasResolveError.value
}

// console.log('aliasResolveData::: ', aliasResolveData)

const traderCode = computed(() => {
  const resolvedData = aliasResolveData.value?.data as Record<string, any>
  const items = resolvedData?.items
  const resolvedCode = normalizeApiText(items?.find((item: any) => item.platform === 2)?.traderCode)

  return resolvedCode
})

/** 品牌级安全措施配置使用 alias-resolve 返回的 brandCode，避免将 traderCode 误作品牌编码。 */
const brandCode = computed(() => {
  const resolvedData = aliasResolveData.value?.data as Record<string, any>

  return normalizeApiText(resolvedData?.brandCode)
})

// console.log('traderCode.value::: ', traderCode.value)
const selectedFundFlowTimeRangeType = ref('5')
const selectedTradingVolumeTimeRangeType = ref('5')
const hasSelectedFundFlowPeriod = ref(false)
const hasSelectedTradingVolumePeriod = ref(false)
const activeFeedbackSearchType = ref(0)
const writeFeedbackBottomOffset = ref(WRITE_FEEDBACK_FOOTER_GAP)
const feedbackPageLabelsCache = ref<WebFeedbackPageLabel[] | null>(null)
const isFeedbackCategorySwitching = ref(false)
const isFeedbackPageLoading = ref(false)
const isFeedbackNextPageChecking = ref(false)
const visibleExchangeFeedbackCount = ref(FEEDBACK_INITIAL_VISIBLE_COUNT)
const isExchangeFeedbackPageEnd = ref(false)
const loadedExchangeFeedbackPageIndex = ref(1)
const currentLocale = computed(() => activeLocale)
const feedbackCategoryCache = new Map<string, FeedbackCategoryCacheEntry>()

const { data: exchangeServerData, error: exchangeServerError } = await useAsyncData<{
  traderDetail: any
}>(
  () => `exchange-server-data-${traderCode.value}-${brandCode.value}-${currentLocale.value}`,
  async () => {
    if (hasInvalidExchangeSlug.value || !traderCode.value) {
      throwExchangeNotFound()
    }

    let traderDetail = null
    try {
      traderDetail = await getTraderDetail(traderCode.value, {
        brandCode: brandCode.value,
      })
      if (!traderDetail) {
        throwExchangeNotFound()
      }
    } catch {
      throwExchangeNotFound()
    }

    return {
      traderDetail,
    }
  },
  {
    default: () => ({
      traderDetail: null,
    }),
    watch: [traderCode, brandCode, currentLocale, hasInvalidExchangeSlug],
  },
)

if (exchangeServerError.value) {
  throw exchangeServerError.value
}

/** 保障弹窗数据不参与首屏阻塞，进入详情页后与其他扩展模块并行加载。 */
const { data: guaranteePopResponse, execute: loadGuaranteePop } = useAsyncData(
  () => `exchange-guarantee-pop-${traderCode.value}`,
  async () => {
    if (!traderCode.value) {
      return null
    }

    return await getGuaranteePop({ traderCode: traderCode.value }).catch(() => ({ data: {} }))
  },
  {
    default: () => null,
    immediate: false,
    lazy: true,
    server: false,
    watch: [traderCode],
  },
)

/** 图表接口只服务于用户切换后的时间区间，首屏沿用详情接口内的图表数据。 */
const { data: traderAssetStrengthChart } = useAsyncData(
  () =>
    `trader-asset-strength-chart-flow-${traderCode.value}-${selectedFundFlowTimeRangeType.value}-${currentLocale.value}`,
  async () => {
    if (!traderCode.value || !hasSelectedFundFlowPeriod.value) {
      return null
    }

    return await getTraderAssetStrengthChart({
      traderCode: traderCode.value,
      timeRangeType: normalizeTimeRangeType(selectedFundFlowTimeRangeType.value),
    })
  },
  {
    default: () => null,
    immediate: false,
    lazy: true,
    server: false,
    watch: [traderCode, selectedFundFlowTimeRangeType, currentLocale, hasSelectedFundFlowPeriod],
  },
)

/** 交易量图表与资金流向保持一致，仅在用户主动切换时间区间后增量加载。 */
const { data: traderTradingVolumeAssetStrengthChart } = useAsyncData(
  () =>
    `trader-asset-strength-chart-volume-${traderCode.value}-${selectedTradingVolumeTimeRangeType.value}-${currentLocale.value}`,
  async () => {
    if (!traderCode.value || !hasSelectedTradingVolumePeriod.value) {
      return null
    }

    return await getTraderAssetStrengthChart({
      traderCode: traderCode.value,
      timeRangeType: normalizeTimeRangeType(selectedTradingVolumeTimeRangeType.value),
    })
  },
  {
    default: () => null,
    immediate: false,
    lazy: true,
    server: false,
    watch: [traderCode, selectedTradingVolumeTimeRangeType, currentLocale, hasSelectedTradingVolumePeriod],
  },
)

const traderDetailData = computed(() => exchangeServerData.value?.traderDetail || null)
const guaranteePopData = computed(() => guaranteePopResponse.value?.data || {})
const licenseProjectCode = computed(
  () => normalizeApiText(traderDetailData.value?.basicInfo?.traderCode) || traderCode.value,
)
const traderFeedback = computed<WebTraderDetailFeedback | null>(() => {
  const feedback = traderDetailData.value?.feedback

  return isRecord(feedback) ? (feedback as WebTraderDetailFeedback) : null
})
const exchangeFeedbackShowType = computed(() => normalizeApiText(traderFeedback.value?.showType))
const getFeedbackCategoryCacheKey = (searchType = activeFeedbackSearchType.value) =>
  `${licenseProjectCode.value}-${currentLocale.value}-${searchType}`
// console.log(traderDetailData.value,"traderDetailData")
const {
  data: feedbackPageData,
  pending: isFeedbackPagePending,
  execute: loadFeedbackPageData,
} = useAsyncData<WebFeedbackPageListData | null>(
  () =>
    `exchange-feedback-page-list-${licenseProjectCode.value}-${activeFeedbackSearchType.value}-${currentLocale.value}`,
  async () => {
    if (!licenseProjectCode.value) {
      return null
    }

    const cacheKey = getFeedbackCategoryCacheKey()
    const cachedCategory = feedbackCategoryCache.get(cacheKey)

    if (cachedCategory) {
      loadedExchangeFeedbackPageIndex.value = cachedCategory.loadedPageIndex
      isExchangeFeedbackPageEnd.value = cachedCategory.isPageEnd
      visibleExchangeFeedbackCount.value = cachedCategory.visibleCount

      return cachedCategory.data
    }

    const response = await getWebFeedbackPageList({
      code: licenseProjectCode.value,
      searchType: activeFeedbackSearchType.value,
      pageIndex: 1,
      pageSize: FEEDBACK_PAGE_SIZE,
    })
    const nextData = normalizeFeedbackPagePayload(response)
    const initialVisibleCount = Math.min(FEEDBACK_INITIAL_VISIBLE_COUNT, nextData?.list?.length ?? 0)
    loadedExchangeFeedbackPageIndex.value = normalizeFeedbackCount(nextData?.pageIndex) || 1
    visibleExchangeFeedbackCount.value = initialVisibleCount
    isExchangeFeedbackPageEnd.value = isFeedbackPageEnd(nextData, nextData?.list?.length ?? 0)

    if (Array.isArray(nextData?.labels) && (!feedbackPageLabelsCache.value || activeFeedbackSearchType.value === 0)) {
      feedbackPageLabelsCache.value = nextData.labels
    }

    const resolvedData = nextData
      ? {
          ...nextData,
          labels: nextData.labels ?? feedbackPageLabelsCache.value,
        }
      : null

    if (resolvedData) {
      feedbackCategoryCache.set(cacheKey, {
        data: resolvedData,
        visibleCount: initialVisibleCount,
        isPageEnd: isExchangeFeedbackPageEnd.value,
        loadedPageIndex: loadedExchangeFeedbackPageIndex.value,
      })
    }

    return resolvedData
  },
  {
    default: () => null,
    immediate: false,
    lazy: true,
    server: false,
    watch: [licenseProjectCode, activeFeedbackSearchType, currentLocale],
  },
)
/** 飞屏独立读取专用接口，不改变反馈 Tab 的分页、分类和缓存数据。 */
const feedbackBulletData = ref<WebFeedbackFlyingScreenResponse | null>(null)
const loadFeedbackBulletData = async () => {
  if (!licenseProjectCode.value) {
    return
  }

  feedbackBulletData.value = await getWebFeedbackFlyingScreen({
    code: licenseProjectCode.value,
    pageSize: FEEDBACK_BULLET_ITEM_LIMIT,
  })
}
const isExchangeFeedbackLoading = computed(
  () => isFeedbackPageLoading.value || isFeedbackNextPageChecking.value || isFeedbackPagePending.value,
)
/** 后台预取不触发可见骨架，避免已展示列表在预取和展开时连续闪烁。 */
const isExchangeFeedbackSkeletonLoading = computed(() => isFeedbackPageLoading.value || isFeedbackPagePending.value)
const traderLicense = computed(() => traderDetailData.value?.license || {})

const regulationItems = computed(() => {
  return traderLicense.value?.data?.items || []
})

const abnormalRegulationItems = computed(() => {
  return traderLicense.value?.data?.abnormalLicenses || []
})

const businessInfo = computed(() => {
  return traderLicense.value?.businessInfo || null
})

const regulationTranslateMap = computed<Record<string, string>>(() => {
  const translateItems = traderLicense.value?.translate?.translate

  if (!Array.isArray(translateItems)) {
    return {}
  }

  return translateItems.reduce<Record<string, string>>((acc, item: LicenseTranslateItem) => {
    const code = normalizeApiText(item?.code)
    const text = normalizeApiText(item?.text)

    if (code && text) {
      acc[code] = text
    }

    return acc
  }, {})
})

const formatDisclosureDate = (value: unknown) => {
  const rawValue = normalizeApiText(value)

  if (!rawValue) {
    return ''
  }

  const timestamp = Number(rawValue)
  if (!Number.isFinite(timestamp) || timestamp <= 0) {
    return rawValue
  }

  const date = new Date(timestamp)
  if (Number.isNaN(date.getTime())) {
    return rawValue
  }

  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(
    2,
    '0',
  )}`
}

const regulationDisclosureList = computed<RegulationDisclosureItem[]>(() => {
  const source = traderDetailData.value?.regulationDisclosure
  const sourceItems = Array.isArray(source)
    ? source
    : Array.isArray(source?.disclosureList)
      ? source.disclosureList
      : Array.isArray(source?.items)
        ? source.items
        : []

  return sourceItems
    .map((item: RegulationDisclosureSourceItem) => {
      const countryText = normalizeApiText(item?.country)
      const countryParts = countryText.split(/\s+/).filter(Boolean)
      const countryName = countryParts[0] || ''
      const regulatorName = countryParts.slice(1).join(' ')
      const matchedTrader =
        (item?.traders || []).find((trader) => normalizeApiText(trader?.code) === normalizeApiText(traderCode.value)) ||
        item?.traders?.[0] ||
        null

      return {
        disclosureId: normalizeApiText(item?.code),
        disclosureAlias: normalizeApiText(item?.alias),
        disclosureType: normalizeApiText(item?.category),
        disclosureTypeColor: normalizeApiText(item?.categoryColor),
        content: normalizeApiText(item?.title),
        countryCode: normalizeApiText(item?.countryCode || item?.CountryCode),
        countryFlag: normalizeApiText(item?.flag),
        countryName,
        regulatorName,
        disclosureDate: formatDisclosureDate(item?.regTime),
        traders: matchedTrader
          ? [
              {
                traderCode: normalizeApiText(matchedTrader?.code),
                traderName:
                  normalizeApiText(matchedTrader?.showName) ||
                  normalizeApiText(matchedTrader?.localShortName) ||
                  normalizeApiText(matchedTrader?.englishShortName) ||
                  normalizeApiText(matchedTrader?.shortName),
                englishFullName: normalizeApiText(matchedTrader?.englishFullName || matchedTrader?.EnglishFullName),
                icon: normalizeApiText(matchedTrader?.logo) || normalizeApiText(matchedTrader?.icon),
              },
            ]
          : [],
      }
    })
    .filter((item) => item.disclosureId || item.content)
})

const ratingCards = computed<TraderRatingCard>(() => {
  return traderDetailData.value?.ratingCards || {}
})

const feeRate = computed<FeeRate>(() => {
  return traderDetailData.value?.feeRate || {}
})

const traderBasicInfo = computed<TraderBasicInfo | null>(() => traderDetailData.value?.basicInfo || null)
/** 黑平台判断统一放在页面层，子组件只消费结果，避免各处重复拼接接口字段条件。 */
const isBlackPlatform = computed(
  () => !traderBasicInfo.value?.isCooperativeCustomer && traderBasicInfo.value?.color === '#B2243C',
)
const traderEmc = computed(() => traderDetailData.value?.emc || null)
const traderRisk = computed(() => traderDetailData.value?.risk || null)
const traderInfluence = computed(() => traderDetailData.value?.marketStrategy || null)
const hasIap = computed(() => hasDetailData(traderDetailData.value?.bitGuarantee))
const hasEmc = computed(() => hasDetailData(traderEmc.value))
const hasRisk = computed(() => hasDetailData(traderRisk.value))
const hasInfluence = computed(() => hasDetailData(traderInfluence.value?.data))
const traderInfluenceRating = computed(() => traderInfluence.value?.data?.influence?.level || '')
const traderInfluenceCountries = computed(() => traderInfluence.value?.data?.influence?.country || [])
/** 资金流向时间 Tab 直接使用交易所详情接口返回的 assetStrength.fundFlowTimeRange。 */
const fundFlowTimeRangeOptions = computed(() =>
  normalizeAssetStrengthTimeRanges(traderDetailData.value?.assetStrength?.fundFlowTimeRange),
)
/** 交易量时间 Tab 与资金流向分别使用详情接口对应字段，避免互相覆盖。 */
const tradingVolumeTimeRangeOptions = computed(() =>
  normalizeAssetStrengthTimeRanges(traderDetailData.value?.assetStrength?.tradingVolumeTimeRange),
)

watch(
  fundFlowTimeRangeOptions,
  (options) => {
    if (options.length === 0) {
      return
    }

    const hasSelectedOption = options.some((option) => option.value === selectedFundFlowTimeRangeType.value)

    if (!hasSelectedOption) {
      selectedFundFlowTimeRangeType.value = options[0].value
    }
  },
  { immediate: true },
)

watch(
  tradingVolumeTimeRangeOptions,
  (options) => {
    if (options.length === 0) {
      return
    }

    const hasSelectedOption = options.some((option) => option.value === selectedTradingVolumeTimeRangeType.value)

    if (!hasSelectedOption) {
      selectedTradingVolumeTimeRangeType.value = options[0].value
    }
  },
  { immediate: true },
)

const exchangeFunds = computed(() => {
  const assetStrength = traderDetailData.value?.assetStrength

  if (!assetStrength) {
    return null
  }

  const fundFlowTimeRanges = fundFlowTimeRangeOptions.value
  const tradingVolumeTimeRanges = tradingVolumeTimeRangeOptions.value
  const assetStrengthChart = traderAssetStrengthChart.value
  const tradingVolumeAssetStrengthChart = traderTradingVolumeAssetStrengthChart.value
  const chartFundFlow = assetStrengthChart?.fundFlow
  const chartTradingVolume = tradingVolumeAssetStrengthChart?.tradingVolume
  const originalFundFlow = assetStrength.fundFlow ?? {}
  const originalTradingVolume = assetStrength.tradingVolume ?? {}
  const originalFundFlowChart = originalFundFlow.chart
  const chartFundFlowChart = chartFundFlow?.chart
  const fundFlowChart =
    hasSelectedFundFlowPeriod.value && getChartPointCount(chartFundFlowChart) > 0
      ? chartFundFlowChart
      : getChartPointCount(originalFundFlowChart) >= getChartPointCount(chartFundFlowChart)
        ? originalFundFlowChart
        : chartFundFlowChart

  const tradingVolumeChart = chartTradingVolume?.chart
  const hasVisibleFundChart =
    hasAssetReserveChartData(assetStrength.assetReserve) ||
    hasFundFlowChartData(fundFlowChart) ||
    hasFundFlowChartData(tradingVolumeChart) ||
    hasFundFlowChartData(originalTradingVolume.chart)

  if (!hasVisibleFundChart) {
    return null
  }

  const updatedTags = Array.isArray(assetStrength.tags)
    ? assetStrength.tags.map((tag: { label?: string; value?: string }) => {
        const tagLabel = normalizeApiText(tag.label)

        if (!tagLabel.includes('资金流向')) {
          return tag
        }

        return {
          ...tag,
          value: normalizeApiText(chartFundFlow?.badge) || tag.value,
        }
      })
    : assetStrength.tags

  return {
    ...assetStrength,
    tags: updatedTags,
    timeRange: fundFlowTimeRanges.length > 0 ? fundFlowTimeRanges : assetStrength.timeRange,
    fundFlowTimeRange: fundFlowTimeRanges.length > 0 ? fundFlowTimeRanges : assetStrength.fundFlowTimeRange,
    tradingVolumeTimeRange:
      tradingVolumeTimeRanges.length > 0 ? tradingVolumeTimeRanges : assetStrength.tradingVolumeTimeRange,
    fundFlow: {
      ...originalFundFlow,
      ...chartFundFlow,
      amount: normalizeApiText(chartFundFlow?.amount) || originalFundFlow.amount,
      changePercent: normalizeApiText(chartFundFlow?.changePercent) || originalFundFlow.changePercent,
      changeDirection: normalizeApiText(chartFundFlow?.changeDirection) || originalFundFlow.changeDirection,
      badge: normalizeApiText(chartFundFlow?.badge) || originalFundFlow.badge,
      chart: fundFlowChart || originalFundFlow.chart,
    },
    tradingVolume: {
      ...originalTradingVolume,
      ...chartTradingVolume,
      chart: tradingVolumeChart || originalTradingVolume.chart,
    },
  }
})
const exchangeTradingVolume = computed(
  () => exchangeFunds.value?.tradingVolume ?? traderDetailData.value?.assetStrength?.tradingVolume ?? null,
)
const exchangeOfficialtokenref = computed(() => traderDetailData.value?.officialToken || null)
const exchangeSupportedcurrenciesref = computed(() => traderDetailData.value?.supportCurrency || null)

const handleFundFlowPeriodChange = (value: string) => {
  hasSelectedFundFlowPeriod.value = true
  selectedFundFlowTimeRangeType.value = value
}

/** 标记交易量时间范围由用户主动变更，以避免首屏额外请求图表接口。 */
const handleTradingVolumePeriodChange = (value: string) => {
  hasSelectedTradingVolumePeriod.value = true
  selectedTradingVolumeTimeRangeType.value = value
}

const traderSurvey = computed(() => traderDetailData.value?.survey || null)
const BASIC_INFO_DISPLAY_ITEM_LABELS: Record<string, string[]> = {
  exchangeShortName: ['exchangeShortName', '交易所简称'],
  companyName: ['companyName', '公司名称'],
  listingInfo: ['listingInfo', 'listedInfo', 'listingStatus', 'ipoInfo', '上市信息'],
  stockCode: ['stockCode', 'stockSymbol', 'tickerSymbol', '股票代码'],
  registeredRegion: ['registeredRegion', 'registeredArea', '注册国/区'],
  cryptoLoan: ['cryptoLoan', '加密货币借贷'],
  operatingYears: ['operatingYears', '经营年限'],
  contactPhone: ['contactPhone', '联系电话', '电话'],
  email: ['email', '客服邮箱'],
  companyAddress: ['companyAddress', '公司地址'],
  website: ['website', '网址'],
  employees: ['employees', 'employee', '员工'],
}
const normalizeDisplayItemLookupValue = (value: unknown) => normalizeApiText(value).toLowerCase()
const findDisplayItem = (items: TraderBasicInfoDisplayItem[] | null | undefined, key: string) => {
  const labels = BASIC_INFO_DISPLAY_ITEM_LABELS[key] || [key]
  const labelSet = new Set(labels.map(normalizeDisplayItemLookupValue))

  return items?.find(
    (item) =>
      labelSet.has(normalizeDisplayItemLookupValue(item.key)) ||
      labelSet.has(normalizeDisplayItemLookupValue(item.label)),
  )
}
const displayItemValue = (items: TraderBasicInfoDisplayItem[] | null | undefined, key: string) =>
  normalizeApiText(findDisplayItem(items, key)?.value)
const displayItemIcon = (items: TraderBasicInfoDisplayItem[] | null | undefined, key: string) =>
  normalizeApiText(findDisplayItem(items, key)?.icon)
const firstBasicInfoValue = (basicInfo: TraderBasicInfo, keys: string[]) =>
  keys.map((key) => normalizeApiText((basicInfo as Record<string, unknown>)[key])).find(Boolean) || ''
const appendUniqueBasicInfoValues = (target: string[], value: unknown) => {
  normalizeApiText(value)
    .split(/\r?\n/)
    .map((item) => item.trim())
    .filter(Boolean)
    .forEach((item) => {
      if (!target.includes(item)) {
        target.push(item)
      }
    })
}
const contactValuesByTypes = (contacts: TraderBasicInfoContact[] | null | undefined, types: number[]) => {
  const typeSet = new Set(types)

  return (
    contacts
      ?.filter((contact) => contact.type !== undefined && typeSet.has(contact.type))
      .map((contact) => normalizeApiText(contact.value))
      .filter(Boolean) || []
  )
}
const firstContactValueByTypes = (contacts: TraderBasicInfoContact[] | null | undefined, types: number[]) =>
  contactValuesByTypes(contacts, types)[0] || ''
const websiteContactLabelKeys = new Set(['website', 'weburl', 'url', 'site', '网址', '企业网址', '官方网站', '官网'])
const normalizeWebsiteContactLabelKey = (value: unknown) =>
  normalizeApiText(value)
    .toLowerCase()
    .replace(/[()\s/_-]+/g, '')
    .replace(/[：:]/g, '')
const isBasicInfoWebsiteContact = (contact: TraderBasicInfoContact) =>
  contact.type === 4 || websiteContactLabelKeys.has(normalizeWebsiteContactLabelKey(contact.label))
const websiteValuesFromBasicInfo = (
  basicInfo: TraderBasicInfo,
  displayItems: TraderBasicInfoDisplayItem[],
  contacts: TraderBasicInfoContact[],
) => {
  const values: string[] = []

  appendUniqueBasicInfoValues(values, displayItemValue(displayItems, 'website'))
  appendUniqueBasicInfoValues(values, basicInfo.website)
  contacts.filter(isBasicInfoWebsiteContact).forEach((contact) => appendUniqueBasicInfoValues(values, contact.value))

  return values
}
const exchangeBasicInfoItems = computed(() => {
  const basicInfo = traderBasicInfo.value

  if (!basicInfo) {
    return []
  }

  const displayItems = basicInfo.displayItems || []
  const contacts = basicInfo.contacts || []
  const registeredRegion = basicInfo.registeredRegion || null
  const registeredAreaValue =
    displayItemValue(displayItems, 'registeredRegion') || normalizeApiText(registeredRegion?.value)
  const exchangeShortNameValue = normalizeApiText(basicInfo.exchangeShortName)
  const companyNameValue = normalizeApiText(basicInfo.companyName)
  const websiteValues = websiteValuesFromBasicInfo(basicInfo, displayItems, contacts)
  const customerServiceEmailValues = contactValuesByTypes(contacts, [3])
  const companyAddressValues = contactValuesByTypes(contacts, [300])
  const items = [
    {
      key: 'exchangeAbbreviation',
      label: '交易所简称',
      value: exchangeShortNameValue && exchangeShortNameValue !== registeredAreaValue ? exchangeShortNameValue : '',
    },
    {
      key: 'regulationStatus',
      label: '监管状态',
      value: normalizeApiText(basicInfo.annotation),
      color: normalizeApiText(basicInfo.annotationColor),
    },
    {
      key: 'companyName',
      label: '公司名称',
      value: companyNameValue,
    },
    {
      key: 'exchangeType',
      label: '交易类型',
      value: normalizeApiText(basicInfo.exchangeType),
    },
    {
      key: 'listingInfo',
      label: '上市信息',
      value:
        displayItemValue(displayItems, 'listingInfo') ||
        firstBasicInfoValue(basicInfo, ['listingInfo', 'listedInfo', 'listingStatus', 'ipoInfo']),
    },
    {
      key: 'stockCode',
      label: '股票代码',
      value:
        displayItemValue(displayItems, 'stockCode') ||
        firstBasicInfoValue(basicInfo, ['stockCode', 'stockSymbol', 'tickerSymbol']),
    },
    {
      key: 'registeredArea',
      label: '注册地区',
      value: registeredAreaValue,
      img: displayItemIcon(displayItems, 'registeredRegion') || normalizeApiText(registeredRegion?.icon),
    },
    {
      key: 'cryptoLoan',
      label: '加密货币借贷',
      value: normalizeApiText(basicInfo.cryptoLoan) || displayItemValue(displayItems, 'cryptoLoan'),
    },
    {
      key: 'operatingYears',
      label: '经营年限',
      value: displayItemValue(displayItems, 'operatingYears') || normalizeApiText(basicInfo.operatingYears),
    },
    {
      key: 'phone',
      label: '联系电话',
      value:
        normalizeApiText(basicInfo.contactPhone) ||
        displayItemValue(displayItems, 'contactPhone') ||
        firstContactValueByTypes(contacts, [1]),
    },
    {
      key: 'customerServiceEmail',
      label: '邮箱',
      values:
        customerServiceEmailValues.length > 0 ? customerServiceEmailValues : displayItemValue(displayItems, 'email'),
    },
    {
      key: 'companyAddress',
      label: '公司地址',
      values: companyAddressValues.length > 0 ? companyAddressValues : displayItemValue(displayItems, 'companyAddress'),
    },
    {
      key: 'website',
      label: '企业网址',
      values: websiteValues,
      linkUrls: websiteValues,
    },
    {
      key: 'employees',
      label: '员工',
      value: displayItemValue(displayItems, 'employees'),
    },
  ]

  return items
})
const contactTypeByApiType: Record<number, string> = {
  2: 'facebook',
  5: 'x',
  7: 'instagram',
  8: 'youtube',
  9: 'linkedin',
  10: 'whatsapp',
  12: 'telegram',
}
const basicInfoFieldContactTypes = new Set([1, 3, 300])
const normalizeBasicInfoContact = (contact: TraderBasicInfoContact) => {
  if (
    (contact.type !== undefined && basicInfoFieldContactTypes.has(contact.type)) ||
    isBasicInfoWebsiteContact(contact)
  ) {
    return null
  }

  const label = normalizeApiText(contact.label)
  const type = contact.type ? contactTypeByApiType[contact.type] : ''
  const value = normalizeApiText(contact.value)

  if (!value) {
    return null
  }

  return {
    key: type || label,
    label: label || type,
    value,
    linkUrl: value,
  }
}
const exchangeBasicInfoContactItems = computed(() => {
  const basicInfo = traderBasicInfo.value

  if (!basicInfo) {
    return []
  }

  const contactItems =
    basicInfo.contacts
      ?.map((contact) => normalizeBasicInfoContact(contact))
      .filter((contact): contact is NonNullable<ReturnType<typeof normalizeBasicInfoContact>> => Boolean(contact)) || []
  const appendedContacts = [
    {
      key: 'x',
      label: 'Twitter',
      value: normalizeApiText(basicInfo.twitter),
      linkUrl: normalizeApiText(basicInfo.twitter),
    },
    {
      key: 'facebook',
      label: 'Facebook',
      value: normalizeApiText(basicInfo.facebook),
      linkUrl: normalizeApiText(basicInfo.facebook),
    },
    {
      key: 'instagram',
      label: 'Instagram',
      value: normalizeApiText(basicInfo.instagram),
      linkUrl: normalizeApiText(basicInfo.instagram),
    },
    {
      key: 'whatsapp',
      label: 'WhatsApp',
      value: normalizeApiText(basicInfo.whatsapp),
      linkUrl: normalizeApiText(basicInfo.whatsapp),
    },
  ].filter((contact) => contact.value)
  const seenKeys = new Set(contactItems.map((contact) => contact.key))

  appendedContacts.forEach((contact) => {
    if (!seenKeys.has(contact.key)) {
      contactItems.push(contact)
      seenKeys.add(contact.key)
    }
  })

  return contactItems
})
const exchangeOverview = computed<TraderExchangeOverview | null>(() => {
  const overview = traderDetailData.value?.exchangeOverview

  return isRecord(overview) ? (overview as TraderExchangeOverview) : null
})
const exchangeOverviewIntroParagraphs = computed(() => {
  const paragraphs = Array.isArray(exchangeOverview.value?.paragraphs)
    ? exchangeOverview.value.paragraphs.map((paragraph) => normalizeApiText(paragraph)).filter(Boolean)
    : []
  const intro = normalizeApiText(exchangeOverview.value?.intro)

  return paragraphs.length > 0 ? paragraphs : intro ? [intro] : []
})
const exchangeOverviewProfileTags = computed(() =>
  (Array.isArray(exchangeOverview.value?.companyImage?.tags) ? exchangeOverview.value.companyImage.tags : [])
    .map((tag: TraderOverviewTag) => {
      const label = normalizeApiText(tag.text)

      if (!label) {
        return null
      }

      return {
        label,
        type: tag.type === 1 ? ('featured' as const) : ('normal' as const),
        icon: normalizeNullableApiText(tag.icon),
        description: normalizeNullableApiText(tag.desc),
        fontColor: normalizeNullableApiText(tag.fontColor),
        backgroundColor: normalizeNullableApiText(tag.backgroundColor),
        borderColor: normalizeNullableApiText(tag.borderColor),
      }
    })
    .filter(
      (
        tag,
      ): tag is {
        label: string
        type: 'featured' | 'normal'
        icon: string | null
        description: string | null
        fontColor: string | null
        backgroundColor: string | null
        borderColor: string | null
      } => Boolean(tag),
    ),
)
type NormalizedExchangeSecurityMeasure = {
  name: string
  title: string | null
  description: string | null
  icon: string | null
  fontColor: string | null
  backgroundColor: string | null
  borderColor: string | null
}

const exchangeOverviewSecurityMeasures = computed(() => {
  const configuredMeasures = Array.isArray(exchangeOverview.value?.securityMeasures?.tags)
    ? exchangeOverview.value.securityMeasures.tags
        .map((tag) => {
          const name = normalizeApiText(tag.text)
          const title = normalizeApiText(tag.title)
          const description = normalizeApiText(tag.desc)

          if (!name) {
            return null
          }

          return {
            name,
            title,
            description,
            icon: normalizeNullableApiText(tag.icon),
            fontColor: normalizeNullableApiText(tag.fontColor),
            backgroundColor: normalizeNullableApiText(tag.backgroundColor),
            borderColor: normalizeNullableApiText(tag.borderColor),
          }
        })
        .filter((measure): measure is NormalizedExchangeSecurityMeasure => Boolean(measure))
    : []
  return configuredMeasures
})
const uniqueTextList = (values: Array<string | null | undefined>) => {
  const seen = new Set<string>()
  const list: string[] = []

  values.forEach((value) => {
    const text = normalizeApiText(value)

    if (!text || seen.has(text)) {
      return
    }

    seen.add(text)
    list.push(text)
  })

  return list
}
const formatFeedbackSubmittedAt = (item: WebFeedbackItem | WebFeedbackPageListItem) => {
  const userInfo = item.userInfo || {}
  const commentInfo = item.tokenGradeCommentInfo || {}
  const rawTime =
    normalizeApiText(userInfo.publishTimeMil) ||
    normalizeApiText(commentInfo.publishTime) ||
    normalizeApiText(userInfo.publishTime)
  const timestamp = Number(rawTime)

  if (!rawTime || Number.isNaN(timestamp)) {
    return rawTime
  }

  return formatRelativeTime(timestamp, currentLocale.value, { translate: t }) || rawTime
}
const firstFeedbackText = (values: Array<string | null | undefined>) =>
  values.map((value) => normalizeApiText(value)).find(Boolean) || ''
const getFeedbackTagText = (commentInfo: WebFeedbackCommentInfo) => {
  const tagFromList =
    commentInfo.tags
      ?.map((tag) => {
        if (typeof tag === 'string') {
          return normalizeApiText(tag)
        }

        return firstFeedbackText([tag.text, tag.name, tag.title])
      })
      .find(Boolean) || ''

  return (
    translateFeedbackText(firstFeedbackText([tagFromList, commentInfo.gradeName, commentInfo.negativeType])) ||
    t('83846')
  )
}
const mapFeedbackImages = (images: WebFeedbackImage[] | null | undefined): FeedbackImage[] =>
  (Array.isArray(images) ? images : []).flatMap((image, index) => {
    if (!image) {
      return []
    }

    const src = firstFeedbackText([image.list, image.detail, image.url])

    if (!src) {
      return []
    }

    return [
      {
        src,
        alt: normalizeApiText(image.word),
      },
    ]
  })
const mapFeedbackEntries = (
  items: Array<WebFeedbackItem | WebFeedbackPageListItem> | null | undefined,
): FeedbackEntry[] =>
  (Array.isArray(items) ? items : []).flatMap((item, index) => {
    const userInfo = item.userInfo || {}
    const commentInfo = item.tokenGradeCommentInfo || {}
    const feedbackId = normalizeApiText(commentInfo.commentGradeId)
    const id = feedbackId || `feedback-${index + 1}`
    const content = firstFeedbackText([
      commentInfo.translateContent,
      commentInfo.content,
      commentInfo.translateTitle,
      commentInfo.title,
    ])
    const tag = getFeedbackTagText(commentInfo)

    if (!content) {
      return []
    }

    return [
      {
        id,
        name: normalizeApiText(userInfo.nickName),
        avatar: normalizeApiText(userInfo.avatarAddress),
        submittedAt: formatFeedbackSubmittedAt(item),
        tag,
        content,
        images: mapFeedbackImages(commentInfo.images || commentInfo.image),
        categoryLabel: tag,
        // 仅在上游返回真实反馈 ID 时生成详情链接，避免回退展示 ID 产生 404。
        detailUrl: feedbackId ? localePath({ name: 'feedback-detail', params: { review_id: id } }) : undefined,
      },
    ]
  })
/** 飞屏直接使用专用接口的 content 字段。 */
const feedbackBulletItems = computed<string[]>(() =>
  normalizeFeedbackFlyingScreenPayload(feedbackBulletData.value)
    .map((item) => normalizeApiText(item.content))
    .filter(Boolean)
    .slice(0, FEEDBACK_BULLET_ITEM_LIMIT),
)
const showFeedbackBulletScreen = computed(
  () => Number(exchangeFeedbackShowType.value) !== 1 && feedbackBulletItems.value.length > 0,
)
const feedbackPreviewItems = computed<FeedbackEntry[]>(() => mapFeedbackEntries(traderFeedback.value?.list))
const feedbackPreviewTotal = computed(() => {
  const total = Number(normalizeApiText(traderFeedback.value?.total))

  return Number.isFinite(total) && total >= 0 ? total : feedbackPreviewItems.value.length
})
const allExchangeFeedbackItems = computed<FeedbackEntry[]>(() => {
  const pageItems = mapFeedbackEntries(feedbackPageData.value?.list)

  return pageItems.length > 0 ? pageItems : feedbackPreviewItems.value
})
const exchangeFeedbackItems = computed<FeedbackEntry[]>(() =>
  allExchangeFeedbackItems.value.slice(0, visibleExchangeFeedbackCount.value),
)
// 分类切换期间不复用上一分类的数据，避免选中态与反馈内容短暂错配。
const displayedExchangeFeedbackItems = computed<FeedbackEntry[]>(() =>
  isFeedbackCategorySwitching.value || isFeedbackPagePending.value ? [] : exchangeFeedbackItems.value,
)
const feedbackCategoryLabels = computed(() => feedbackPageLabelsCache.value ?? feedbackPageData.value?.labels ?? [])
const feedbackPageReportedTotal = computed(() => {
  const pageSize = normalizeFeedbackCount(feedbackPageData.value?.pageSize)
  const pageCount = normalizeFeedbackCount(feedbackPageData.value?.pageCount)
  const totals = [
    feedbackPageData.value?.showTotal,
    feedbackPageData.value?.total,
    pageSize > 0 && pageCount > 0 ? pageSize * pageCount : Number.NaN,
  ]
    .map((value) => {
      const text = normalizeApiText(value)

      return text ? Number(text) : Number.NaN
    })
    .filter((value) => Number.isFinite(value) && value > 0)

  return totals.length > 0 ? Math.max(...totals) : 0
})
const exchangeFeedbackAllTotal = computed(() => {
  const feedbackLabels = feedbackCategoryLabels.value
  const labelCounts = feedbackLabels.map((label) => normalizeFeedbackCount(label?.count)).filter((count) => count > 0)
  const nonAllLabelTotal = feedbackLabels
    .filter((label) => feedbackTextKeyMap[normalizeApiText(label?.name) || normalizeApiText(label?.text)] !== '83794')
    .reduce((total, label) => total + normalizeFeedbackCount(label?.count), 0)
  const allLabelCount = feedbackLabels.find((label) => normalizeFeedbackSearchType(label?.value) === 0)?.count
  const totals = [
    feedbackPageReportedTotal.value,
    allLabelCount,
    ...labelCounts,
    nonAllLabelTotal,
    feedbackPreviewTotal.value,
  ]
    .map((value) => {
      const text = normalizeApiText(value)

      return text ? Number(text) : Number.NaN
    })
    .filter((value) => Number.isFinite(value) && value >= 0)

  return Math.max(...totals, allExchangeFeedbackItems.value.length)
})
const exchangeFeedbackCategories = computed<FeedbackCategory[] | undefined>(() => {
  const labels = feedbackCategoryLabels.value

  if (!Array.isArray(labels) || labels.length === 0) {
    return undefined
  }

  const categories = labels.flatMap((label) => {
    if (!label || typeof label !== 'object') {
      return []
    }

    const category = label as WebFeedbackPageLabel
    const categoryLabel = translateFeedbackText(normalizeApiText(category.name) || normalizeApiText(category.text))
    const categoryValue = normalizeFeedbackSearchType(category.value)
    const categoryCount = categoryValue === 0 ? exchangeFeedbackAllTotal.value : normalizeFeedbackCount(category.count)

    if (!categoryLabel) {
      return []
    }

    return [
      {
        key: categoryValue === 0 ? 'all' : categoryLabel,
        label: categoryLabel,
        count: categoryCount,
        value: category.value,
      },
    ]
  })

  return categories.length > 0 ? categories : undefined
})
const exchangeFeedbackTitle = computed(
  () => translateFeedbackText(normalizeApiText(traderFeedback.value?.title)) || t('83846'),
)
const exchangeFeedbackTotal = computed(() => {
  const feedbackLabels = feedbackPageData.value?.labels ?? []
  const activeLabelCount =
    activeFeedbackSearchType.value === 0
      ? Number.NaN
      : normalizeFeedbackCount(
          feedbackLabels.find((label) => normalizeFeedbackSearchType(label?.value) === activeFeedbackSearchType.value)
            ?.count,
        )

  if (Number.isFinite(activeLabelCount) && activeLabelCount > 0) {
    return Math.max(activeLabelCount, allExchangeFeedbackItems.value.length)
  }

  const labelCounts = feedbackLabels.map((label) => normalizeFeedbackCount(label?.count)).filter((count) => count > 0)
  const nonAllLabelTotal = feedbackLabels
    .filter((label) => feedbackTextKeyMap[normalizeApiText(label?.name) || normalizeApiText(label?.text)] !== '83794')
    .reduce((total, label) => total + normalizeFeedbackCount(label?.count), 0)
  const allLabelCount = feedbackLabels.find((label) => normalizeFeedbackSearchType(label?.value) === 0)?.count
  const totals = [
    feedbackPageData.value?.showTotal,
    allLabelCount,
    feedbackPageData.value?.total,
    ...labelCounts,
    nonAllLabelTotal,
    feedbackPreviewTotal.value,
  ]
    .map((value) => {
      const text = normalizeApiText(value)

      return text ? Number(text) : Number.NaN
    })
    .filter((value) => Number.isFinite(value) && value >= 0)

  return Math.max(...totals, allExchangeFeedbackItems.value.length)
})
const exchangeFeedbackNotice = computed(() => normalizeApiText(traderFeedback.value?.tips) || t('84167'))
const feedbackTabLabel = computed(() => t('83846'))
const hasHiddenLoadedExchangeFeedback = computed(
  () => visibleExchangeFeedbackCount.value < allExchangeFeedbackItems.value.length,
)
const visibleExchangeFeedbackPage = computed(() =>
  Math.max(1, Math.ceil(visibleExchangeFeedbackCount.value / FEEDBACK_PAGE_SIZE)),
)
const hasNextExchangeFeedbackPage = computed(() => {
  if (isExchangeFeedbackPageEnd.value) {
    return false
  }

  return allExchangeFeedbackItems.value.length > 0
})
const hasMoreExchangeFeedback = computed(
  () => hasHiddenLoadedExchangeFeedback.value || hasNextExchangeFeedbackPage.value,
)
const canAutoLoadExchangeFeedback = computed(
  () => hasMoreExchangeFeedback.value && visibleExchangeFeedbackPage.value < FEEDBACK_AUTO_PAGE_LIMIT,
)
const showExchangeFeedbackLoadMore = computed(
  () => hasHiddenLoadedExchangeFeedback.value && visibleExchangeFeedbackPage.value >= FEEDBACK_AUTO_PAGE_LIMIT,
)
const shouldPrefetchExchangeFeedbackLoadMore = computed(
  () =>
    hasNextExchangeFeedbackPage.value &&
    !hasHiddenLoadedExchangeFeedback.value &&
    visibleExchangeFeedbackPage.value >= FEEDBACK_AUTO_PAGE_LIMIT,
)
const resetExchangeFeedbackPaging = () => {
  visibleExchangeFeedbackCount.value = FEEDBACK_INITIAL_VISIBLE_COUNT
  isExchangeFeedbackPageEnd.value = false
  isFeedbackNextPageChecking.value = false
  loadedExchangeFeedbackPageIndex.value = 1
}
const cacheCurrentFeedbackCategory = () => {
  if (!licenseProjectCode.value || !feedbackPageData.value) {
    return
  }

  feedbackCategoryCache.set(getFeedbackCategoryCacheKey(), {
    data: feedbackPageData.value,
    visibleCount: visibleExchangeFeedbackCount.value,
    isPageEnd: isExchangeFeedbackPageEnd.value,
    loadedPageIndex: loadedExchangeFeedbackPageIndex.value,
  })
}
const handleFeedbackCategorySelect = (category: FeedbackCategory) => {
  const nextSearchType = category.key === 'all' ? 0 : normalizeFeedbackSearchType(category.value)

  if (nextSearchType === activeFeedbackSearchType.value) {
    return
  }

  cacheCurrentFeedbackCategory()
  const cachedCategory = feedbackCategoryCache.get(getFeedbackCategoryCacheKey(nextSearchType))

  if (cachedCategory) {
    isFeedbackCategorySwitching.value = false
    activeFeedbackSearchType.value = nextSearchType
    feedbackPageData.value = cachedCategory.data
    visibleExchangeFeedbackCount.value = cachedCategory.visibleCount
    isExchangeFeedbackPageEnd.value = cachedCategory.isPageEnd
    loadedExchangeFeedbackPageIndex.value = cachedCategory.loadedPageIndex
    return
  }

  isFeedbackCategorySwitching.value = true
  activeFeedbackSearchType.value = nextSearchType
  resetExchangeFeedbackPaging()
}
const requestNextExchangeFeedbackPage = async () => {
  if (!licenseProjectCode.value || isExchangeFeedbackPageEnd.value) {
    return false
  }

  const currentData = feedbackPageData.value
  const response = await getWebFeedbackPageList({
    code: licenseProjectCode.value,
    searchType: activeFeedbackSearchType.value,
    pageIndex: loadedExchangeFeedbackPageIndex.value + 1,
    pageSize: FEEDBACK_PAGE_SIZE,
  })
  const nextData = normalizeFeedbackPagePayload(response)
  const nextList = nextData?.list ?? []

  if (!nextData || nextList.length === 0) {
    isExchangeFeedbackPageEnd.value = true
    return false
  }

  loadedExchangeFeedbackPageIndex.value =
    normalizeFeedbackCount(nextData.pageIndex) || loadedExchangeFeedbackPageIndex.value + 1
  const accumulatedCount = (currentData?.list?.length ?? 0) + nextList.length
  isExchangeFeedbackPageEnd.value = isFeedbackPageEnd(nextData, accumulatedCount)
  feedbackPageData.value = {
    ...currentData,
    ...nextData,
    labels: nextData.labels ?? currentData?.labels ?? feedbackPageLabelsCache.value,
    list: [...(currentData?.list ?? []), ...nextList],
  }

  return true
}
const prefetchExchangeFeedbackLoadMore = async () => {
  if (isExchangeFeedbackLoading.value || !shouldPrefetchExchangeFeedbackLoadMore.value) {
    return
  }

  isFeedbackNextPageChecking.value = true

  try {
    await requestNextExchangeFeedbackPage()
  } finally {
    isFeedbackNextPageChecking.value = false
  }
}
const loadMoreExchangeFeedback = async (manual = false) => {
  if (isExchangeFeedbackLoading.value || !hasMoreExchangeFeedback.value) {
    return
  }

  if (!manual && !canAutoLoadExchangeFeedback.value) {
    return
  }

  if (visibleExchangeFeedbackCount.value < allExchangeFeedbackItems.value.length) {
    visibleExchangeFeedbackCount.value = Math.min(
      visibleExchangeFeedbackCount.value + FEEDBACK_PAGE_SIZE,
      allExchangeFeedbackItems.value.length,
      exchangeFeedbackTotal.value,
    )
    return
  }

  isFeedbackPageLoading.value = true

  try {
    const hasNextPageData = await requestNextExchangeFeedbackPage()

    if (!hasNextPageData) {
      return
    }

    visibleExchangeFeedbackCount.value = Math.min(
      visibleExchangeFeedbackCount.value + FEEDBACK_PAGE_SIZE,
      (feedbackPageData.value.list ?? []).length,
    )
  } finally {
    isFeedbackPageLoading.value = false
  }
}
const handleExchangeFeedbackLoadMore = async (manual = true) => {
  await loadMoreExchangeFeedback(manual)
}
const riskDialogVisible = ref(false)
const closeRiskDialog = () => {
  riskDialogVisible.value = false
  matomo.trackEvent('broker_risk_pop', {
    name: traderBasicInfo.value?.showName || '',
    code: traderBasicInfo.value?.traderCode || traderCode.value || '',
  })
}
const influenceDialogVisible = ref(false)
const closeInfluenceDialog = () => {
  influenceDialogVisible.value = false
  matomo.trackEvent('broker_influence_pop', {
    name: traderBasicInfo.value?.showName?.toString() || '',
    code: traderBasicInfo.value?.traderCode || traderCode.value || '',
  })
}
const licenseDialogVisible = ref(false)
const licenseDialogLoading = ref(false)
const selectedLicenseSummary = ref<any>(null)
const selectedLicenseDetail = ref<LicenseDetail | null>(null)
const emcDialogVisible = ref(false)
const closeEmcDialog = () => {
  emcDialogVisible.value = false
  matomo.trackEvent('broker_emc_pop', {
    af_type: '关闭',
    name: traderBasicInfo.value?.showName || '',
    code: traderBasicInfo.value?.traderCode || traderCode.value || '',
  })
}
const iapDialogVisible = ref(false)
const emcFeedbackDialogVisible = ref(false)
let licenseDetailRequestId = 0
const feedbackDialogVisible = ref(false)

const showOverviewModule = computed(() => Boolean(exchangeOverview.value))
const showFundsModule = computed(() => Boolean(exchangeFunds.value))
const showOfficialtokenModule = computed(() => Boolean(exchangeOfficialtokenref.value))
const showSupportedcurrenciesModule = computed(() => Boolean(exchangeSupportedcurrenciesref.value))
const showBusinessAreaModule = computed(() => traderInfluenceCountries.value.length > 0)
const showSiteVisitModule = computed(() => (traderSurvey.value?.items?.length || 0) > 0)
const showBackgroundInvestigationModule = computed(() => false)
const showDepositWithdrawalModule = computed(() => traderDetailData.value?.bitDepositAndWithdrawal)
const showFeeRateModule = computed(() => true)
const showRegulationDisclosureModule = computed(() => regulationDisclosureList.value.length > 0)
/** 用户反馈接口未返回列表数据时，同步隐藏导航 Tab 和反馈内容区。 */
const showFeedbackModule = computed(() => (feedbackPageData.value?.list?.length || 0) > 0)
const showFeedbackPreviewModule = computed(
  () =>
    Number(exchangeFeedbackShowType.value) !== 1 &&
    (feedbackPreviewItems.value.length > 0 || feedbackPreviewTotal.value > 0),
)
const showExchangeFeedbackCount = computed(() => Number(exchangeFeedbackShowType.value) !== 1)
const exchangeLogo = computed(() => normalizeApiText(traderBasicInfo.value?.ico) || '')
const exchangeName = computed(() => {
  const basicInfo = traderBasicInfo.value
  const shortName = normalizeApiText(basicInfo?.shortName)

  return shortName || traderCode.value || ''
})
const shouldShowWriteFeedbackEntry = computed(() => Boolean(traderCode.value || exchangeName.value))
const resolveExchangeSeoText = (key: string) => t(key, [exchangeName.value]).replaceAll('{0}', exchangeName.value)
const exchangeSeoTitle = computed(() => {
  const title = resolveExchangeSeoText('84373')

  return title.includes('WikiBit') ? title : `${title} | WikiBit`
})

useHead(() => ({
  title: exchangeSeoTitle.value,
  meta: [
    {
      name: 'description',
      content: resolveExchangeSeoText('84358'),
    },
  ],
}))

// 普通旗舰店不启用 special 头部态，其他旗舰店类型统一启用。
const special = computed(() => Number(traderDetailData.value?.basicInfo?.ultimateType) !== 1)

const detailSectionRegistry = computed(() => [
  {
    key: 'funds',
    label: '交易所资金',
    localeKey: '84554',
    targetId: 'exchange-funds-tab',
    isVisible: showFundsModule.value,
  },
  {
    key: 'officialtoken',
    label: '官方代币',
    localeKey: '82663',
    targetId: 'exchange-officialtoken-tab',
    isVisible: showOfficialtokenModule.value,
  },
  {
    key: 'supported-currencies',
    label: '支持币种',
    localeKey: '82740',
    targetId: 'exchange-supported-currencies-tab',
    isVisible: showSupportedcurrenciesModule.value,
  },
  {
    key: 'overview',
    label: '交易所概况',
    localeKey: '82583',
    targetId: 'exchange-overview-tab',
    isVisible: showOverviewModule.value,
  },
  {
    key: 'business-area',
    label: '展业区域',
    localeKey: '82587',
    targetId: 'exchange-business-area-tab',
    isVisible: showBusinessAreaModule.value,
  },
  {
    key: 'background-investigation',
    label: '背景调查',
    targetId: 'exchange-background-investigation-tab',
    isVisible: showBackgroundInvestigationModule.value,
  },
  {
    key: 'deposit-withdrawal',
    label: '充值费用',
    localeKey: '84556',
    targetId: 'exchange-deposit-withdrawal-tab',
    isVisible: showDepositWithdrawalModule.value,
  },
  // {
  //   key: 'fee-rate',
  //   label: '交易费率',
  //   targetId: 'exchange-fee-rate-tab',
  //   isVisible: showFeeRateModule.value,
  // },
  {
    key: 'regulation-disclosure',
    label: '监管披露',
    localeKey: '82718',
    targetId: 'exchange-regulation-disclosure-tab',
    isVisible: showRegulationDisclosureModule.value,
  },
  {
    key: 'feedback',
    label: feedbackTabLabel.value,
    targetId: 'exchange-feedback-tab',
    isVisible: showFeedbackModule.value,
  },
])

const visibleDetailTabs = computed(() =>
  detailSectionRegistry.value
    .filter((section) => section.isVisible)
    .map((section) => ({
      key: section.key,
      label: section.label,
      localeKey: section.localeKey,
    })),
)
const activeDetailTabKey = ref('funds')
const detailTabsOffset = 150
let detailTabSelectTimer: number | null = null
let detailPageScrollFrame: number | null = null
/** 控制回到顶部入口，仅在页面已向下滚动时显示。 */
const isBackToTopVisible = ref(false)

const openFeedbackDialog = () => {
  emcDialogVisible.value = false
  feedbackDialogVisible.value = true
}

// 有反馈数据且尚未定位到反馈模块时，优先带用户查看反馈列表；其余情况沿用原有弹窗逻辑。
const handleWriteFeedbackClick = () => {
  if (exchangeFeedbackTotal.value > 0 && activeDetailTabKey.value !== 'feedback') {
    void handleDetailTabSelect('feedback')
    return
  }

  openFeedbackDialog()
}

const handleEmcFeedback = () => {
  openFeedbackDialog()
  matomo.trackEvent('broker_emc_pop', {
    af_type: '反馈',
    name: traderBasicInfo.value?.showName,
    code: traderBasicInfo.value?.traderCode || traderCode.value || '',
  })
}

const getLicenseData = (regulationId: string) => {
  const key = `${routeAlias}_${currentLocale.value}_${CountryCode}_licenseData_${regulationId}`
  const raw = window.localStorage.getItem(key)
  if (!raw) return null
  const { data, expire } = JSON.parse(raw)
  if (Date.now() > expire) {
    window.localStorage.removeItem(key)
    return null
  }
  return data
}

const openLicenseDialog = async (item: any) => {
  const regulationId = normalizeApiText(item?.id || item?.regulationId || item?.licenseNumber)

  if (!regulationId) {
    return
  }

  const requestId = licenseDetailRequestId + 1
  licenseDetailRequestId = requestId
  selectedLicenseSummary.value = item
  selectedLicenseDetail.value = null
  licenseDialogVisible.value = true
  licenseDialogLoading.value = true

  try {
    const cachedDetail = getLicenseData(regulationId)
    let detail = null
    if (cachedDetail?.regulationId) {
      detail = cachedDetail
    } else {
      detail = await getLicenseDetail(item?.projectCode || licenseProjectCode.value, regulationId)
    }

    if (licenseDetailRequestId === requestId) {
      selectedLicenseDetail.value = detail
    }
  } catch {
    if (licenseDetailRequestId === requestId) {
      selectedLicenseDetail.value = null
    }
  } finally {
    if (licenseDetailRequestId === requestId) {
      licenseDialogLoading.value = false
    }
  }
}

const getDetailSectionTarget = (key: string) => {
  const targetId = detailSectionRegistry.value.find((section) => section.key === key)?.targetId

  if (!targetId || typeof document === 'undefined') {
    return null
  }

  return document.getElementById(targetId)
}

const updateActiveDetailTab = () => {
  if (typeof window === 'undefined') {
    return
  }

  const visibleSections = detailSectionRegistry.value.filter((section) => section.isVisible)
  const lastVisibleSection = visibleSections[visibleSections.length - 1]

  if (!lastVisibleSection) {
    return
  }

  const pageBottom = document.documentElement.scrollHeight - window.innerHeight

  if (window.scrollY >= pageBottom - 2) {
    activeDetailTabKey.value = lastVisibleSection.key
    return
  }

  // 模块顶部进入吸顶导航下方的可视区域时提前切换，避免仍停留在上一项。
  const activationLine = detailTabsOffset + 50
  let activeSection = visibleSections[0]
  let closestUpcomingSection = visibleSections[0]
  let closestUpcomingDistance = Number.POSITIVE_INFINITY
  let hasPassedSection = false

  visibleSections.forEach((section) => {
    const target = getDetailSectionTarget(section.key)

    if (!target) {
      return
    }

    const distanceToActivationLine = target.getBoundingClientRect().top - activationLine

    if (distanceToActivationLine <= 1) {
      activeSection = section
      hasPassedSection = true
      return
    }

    if (!hasPassedSection && distanceToActivationLine < closestUpcomingDistance) {
      closestUpcomingSection = section
      closestUpcomingDistance = distanceToActivationLine
    }
  })

  activeDetailTabKey.value = (hasPassedSection ? activeSection : closestUpcomingSection)?.key || lastVisibleSection.key
}

const updateWriteFeedbackButtonOffset = () => {
  if (typeof window === 'undefined') {
    return
  }

  const footerElement = document.querySelector<HTMLElement>('.wiki-bit-pc-bootom-map')
  const footerOverlap = footerElement ? Math.max(0, window.innerHeight - footerElement.getBoundingClientRect().top) : 0

  writeFeedbackBottomOffset.value = WRITE_FEEDBACK_FOOTER_GAP + footerOverlap
}

/** 根据当前滚动位置同步回到顶部入口的显示状态。 */
const updateBackToTopVisibility = () => {
  if (typeof window === 'undefined') {
    return
  }

  isBackToTopVisible.value = window.scrollY > 0
}

const handleDetailPageScroll = () => {
  if (detailPageScrollFrame !== null || typeof window === 'undefined') {
    return
  }

  detailPageScrollFrame = window.requestAnimationFrame(() => {
    detailPageScrollFrame = null
    updateActiveDetailTab()
    updateWriteFeedbackButtonOffset()
    updateBackToTopVisibility()
  })
}

const fundsMorePath = computed(() =>
  localePath({
    name: 'exchange-fund-security',
    params: {
      slug: exchangeSlug.value,
    },
  }),
)

const handleDetailTabSelect = async (key: string) => {
  activeDetailTabKey.value = key
  await nextTick()

  const target = getDetailSectionTarget(key)

  if (!target) {
    return
  }

  window.scrollTo({
    top: Math.max(target.getBoundingClientRect().top + window.scrollY - detailTabsOffset, 0),
    behavior: 'smooth',
  })

  if (detailTabSelectTimer) {
    window.clearTimeout(detailTabSelectTimer)
  }

  detailTabSelectTimer = window.setTimeout(() => {
    activeDetailTabKey.value = key
    updateActiveDetailTab()
    detailTabSelectTimer = null
  }, 450)
}

/** 滚动至页面顶部，供右侧悬浮的 TOP 快捷入口使用。 */
const handleBackToTopClick = () => {
  window.scrollTo({
    top: 0,
    behavior: 'smooth',
  })
}

let isDetailScrollListening = false

onMounted(() => {
  // 水合完成后并行加载非核心模块，避免扩展接口阻塞导航或造成 SSR 首帧不一致。
  void Promise.allSettled([loadGuaranteePop(), loadFeedbackPageData(), loadFeedbackBulletData()])

  matomo.trackEvent('broker_details', {
    af_name: traderDetailData.value?.basicInfo?.shortName,
    af_code: exchangeSlug.value,
  })
  updateActiveDetailTab()
  updateWriteFeedbackButtonOffset()
  updateBackToTopVisibility()

  if (typeof window !== 'undefined') {
    window.addEventListener('scroll', handleDetailPageScroll, { passive: true })
    window.addEventListener('resize', handleDetailPageScroll)
    isDetailScrollListening = true
  }
})

onBeforeUnmount(() => {
  if (!isDetailScrollListening || typeof window === 'undefined') {
    return
  }

  window.removeEventListener('scroll', handleDetailPageScroll)
  window.removeEventListener('resize', handleDetailPageScroll)

  if (detailTabSelectTimer) {
    window.clearTimeout(detailTabSelectTimer)
    detailTabSelectTimer = null
  }

  if (detailPageScrollFrame !== null) {
    window.cancelAnimationFrame(detailPageScrollFrame)
    detailPageScrollFrame = null
  }
})

watch(
  visibleDetailTabs,
  (tabs) => {
    if (tabs.length === 0) {
      activeDetailTabKey.value = ''
      return
    }

    if (!tabs.some((tab) => tab.key === activeDetailTabKey.value)) {
      activeDetailTabKey.value = tabs[0]?.key || ''
    }
  },
  { immediate: true },
)

watch(
  shouldPrefetchExchangeFeedbackLoadMore,
  (shouldPrefetch) => {
    if (shouldPrefetch) {
      prefetchExchangeFeedbackLoadMore()
    }
  },
  { flush: 'post' },
)

watch(isFeedbackPagePending, (pending) => {
  if (!pending) {
    isFeedbackCategorySwitching.value = false
  }
})
</script>

<template>
  <HeaderWikiBitPcTopNav
    v-if="!isExchangeLicenseRoute"
    :always-expand-search="false"
    :force-solid-background="!isExchangeDetailRootRoute"
    :special="isExchangeDetailRootRoute && special"
    :ultra="traderBasicInfo?.ultimateType == 3"
  />
  <NuxtPage v-if="!isExchangeDetailRootRoute" />

  <main
    v-else
    class="exchange-detail-page"
    :style="{ '--write-feedback-bottom-safe-area': `${writeFeedbackBottomOffset}px` }"
  >
    <ExchangeDetailHero :trader-data="traderDetailData" :is-black-platform="isBlackPlatform" />

    <section class="exchange-detail-container exchange-detail-main">
      <div id="exchange-detail-basic" class="exchange-detail-top-grid">
        <div class="exchange-detail-column exchange-detail-column-main">
          <ExchangeDetailExchangeIdentityPanel
            :trader-data="traderDetailData"
            :trader-code="traderCode"
            :exchange-slug="exchangeSlug"
            :is-black-platform="isBlackPlatform"
          />
          <ExchangeDetailBasicInfoCard
            :info-items="exchangeBasicInfoItems"
            :contact-items="exchangeBasicInfoContactItems"
            :exchange-name="traderBasicInfo?.showName || traderBasicInfo?.localShortName || ''"
            :exchange-id="traderCode"
          />
        </div>

        <div class="exchange-detail-column exchange-detail-column-aside z-1 !min-w-[250px]">
          <ExchangeDetailExchangeRatingCard
            :rating-cards="ratingCards"
            :exchange-name="traderBasicInfo?.showName || traderBasicInfo?.localShortName || ''"
            :exchange-id="traderCode"
            :ultimate-type="traderBasicInfo?.ultimateType"
            @open-iap="iapDialogVisible = true"
          />

          <ExchangeDetailExchangeIapCard
            v-if="hasIap && !hasEmc"
            :trader-data="traderDetailData"
            :trader-code="traderCode"
            :guarantee-pop="guaranteePopData"
            @open="iapDialogVisible = true"
          />
          <ExchangeDetailExchangeEmcCard
            v-if="hasEmc"
            @open="emcDialogVisible = true"
            @open-iap="iapDialogVisible = true"
            :trader-data="traderDetailData"
          />
          <ExchangeDetailExchangeRiskCard
            v-else-if="hasRisk && !hasEmc && !hasIap"
            :risk="traderRisk"
            @open="riskDialogVisible = true"
          />

          <ExchangeDetailExchangeInfluenceCard
            v-else-if="hasInfluence && !hasEmc && !hasIap && !hasRisk"
            :marketStrategy="traderInfluence?.data"
            @open="influenceDialogVisible = true"
          />
          <ExchangeDetailExchangeTradingVolumeCard
            v-else-if="!hasInfluence && !hasEmc && !hasIap && !hasRisk"
            :trading-volume="exchangeTradingVolume"
            :to="fundsMorePath"
          />
        </div>

        <div class="exchange-detail-column exchange-detail-column-regulation">
          <ExchangeDetailExchangeRegulationCard
            :items="regulationItems"
            :abnormal-licenses="abnormalRegulationItems"
            :business-info="businessInfo"
            :translate-map="regulationTranslateMap"
            :project-code="licenseProjectCode"
            :exchange-name="traderBasicInfo?.showName || traderBasicInfo?.localShortName || ''"
            @open="openLicenseDialog"
          />
        </div>
      </div>

      <ExchangeDetailSiteVisitCard
        v-if="showSiteVisitModule || showFeedbackPreviewModule"
        id="exchange-site-visit-tab"
        class="exchange-detail-site-visit"
        :survey="traderSurvey"
        :feedback-items="feedbackPreviewItems"
        :feedback-count="feedbackPreviewTotal"
        :show-feedback="showFeedbackPreviewModule"
        :show-feedback-count="showExchangeFeedbackCount"
        :show-visit-map="Number(exchangeFeedbackShowType) === 1"
        :exchange-name="traderBasicInfo?.fullName || traderBasicInfo?.localShortName"
        :exchange-id="traderCode"
        data-exchange-detail-section="site-visit"
      />

      <ExchangeDetailWriteFeedback
        v-if="shouldShowWriteFeedbackEntry"
        class="exchange-detail-write-feedback"
        :style="{ '--write-feedback-bottom-safe-area': `${writeFeedbackBottomOffset}px` }"
        :label="t('82430')"
        :feedback-count="exchangeFeedbackTotal"
        :exchange-name="traderBasicInfo?.showName || traderBasicInfo?.localShortName || ''"
        :exchange-id="traderCode"
        @click="handleWriteFeedbackClick"
      />

      <ExchangeDetailTabs
        v-if="visibleDetailTabs.length > 0"
        class="exchange-detail-tabs-bar"
        :tabs="visibleDetailTabs"
        :active-key="activeDetailTabKey"
        :exchange-name="traderBasicInfo?.showName || traderBasicInfo?.localShortName || ''"
        :exchange-id="traderCode"
        @select="handleDetailTabSelect"
      />
      <ExchangeDetailFundsCard
        v-if="showFundsModule"
        id="exchange-funds-tab"
        class="exchange-detail-funds"
        :asset-strength="exchangeFunds"
        :exchange-icon="exchangeLogo"
        :exchange-name="exchangeName"
        :exchange-id="traderCode"
        :more-path="fundsMorePath"
        :selected-flow-time-range-type="selectedFundFlowTimeRangeType"
        :selected-volume-time-range-type="selectedTradingVolumeTimeRangeType"
        data-exchange-detail-section="funds"
        @flow-period-change="handleFundFlowPeriodChange"
        @volume-period-change="handleTradingVolumePeriodChange"
      />
      <ExchangeDetailExchangeOfficialtoken
        v-if="showOfficialtokenModule"
        id="exchange-officialtoken-tab"
        class="exchange-detail-officialtoken"
        data-exchange-detail-section="officialtoken"
        :exchange-name="traderBasicInfo?.showName || traderBasicInfo?.localShortName || ''"
        :exchange-id="traderCode"
        :official-token="exchangeOfficialtokenref"
      />
      <ExchangeDetailExchangeSupportedcurrencies
        v-if="showSupportedcurrenciesModule"
        id="exchange-supported-currencies-tab"
        class="exchange-detail-supported-currencies"
        data-exchange-detail-section="supported-currencies"
        :exchange-name="traderBasicInfo?.showName || traderBasicInfo?.localShortName || ''"
        :exchange-id="traderCode"
        :exchange-slug="exchangeSlug"
        :supported-currencies="exchangeSupportedcurrenciesref"
      />
      <ExchangeDetailOverviewCard
        v-if="showOverviewModule"
        class="exchange-detail-overview"
        :intro-paragraphs="exchangeOverviewIntroParagraphs"
        :profile-tags="exchangeOverviewProfileTags"
        :security-measures="exchangeOverviewSecurityMeasures"
        :exchange-name="traderBasicInfo?.showName || traderBasicInfo?.localShortName || ''"
        :exchange-id="traderCode"
        data-exchange-detail-section="overview"
      />
      <ExchangeDetailBusinessAreaCard
        v-if="showBusinessAreaModule"
        id="exchange-business-area-tab"
        class="exchange-detail-business-area"
        :influence-rating="traderInfluenceRating"
        :market-influence-countries="traderInfluenceCountries"
        data-exchange-detail-section="business-area"
      />
      <ExchangeDetailExchangeBackgroundInvestigationCard
        v-if="showBackgroundInvestigationModule"
        id="exchange-background-investigation-tab"
        class="exchange-detail-background-investigation"
      />
      <ExchangeDetailExchangeDepositWithdrawalCard
        v-if="showDepositWithdrawalModule"
        id="exchange-deposit-withdrawal-tab"
        class="exchange-detail-deposit-withdrawal"
        :trader-data="traderDetailData"
        data-exchange-detail-section="deposit-withdrawal"
      />
      <!-- 交易费率(现货交易+合约交易+法币交易+充提币) -->
      <!-- <ExchangeDetailExchangeFeeRate
        v-if="showFeeRateModule"
        id="exchange-fee-rate-tab"
        class="exchange-detail-fee-rate"
        data-exchange-detail-section="fee-rate"
        :data="feeRate"
        :exchange-id="traderCode"
      /> -->
      <ExchangeDetailExchangeRegulationDisclosureCard
        v-if="showRegulationDisclosureModule"
        id="exchange-regulation-disclosure-tab"
        class="exchange-detail-regulation-disclosure"
        data-exchange-detail-section="regulation-disclosure"
        :disclosures="regulationDisclosureList"
        :project-code="licenseProjectCode"
      />
      <ExchangeDetailFeedbackPageList
        v-if="showFeedbackModule"
        id="exchange-feedback-tab"
        class="exchange-detail-feedback"
        data-exchange-detail-section="feedback"
        :title="exchangeFeedbackTitle"
        :total="exchangeFeedbackTotal"
        :notice="exchangeFeedbackNotice"
        :categories="exchangeFeedbackCategories"
        :feedback-items="displayedExchangeFeedbackItems"
        :loading="isExchangeFeedbackSkeletonLoading || isFeedbackCategorySwitching"
        :replace-on-loading="isFeedbackCategorySwitching || isFeedbackPagePending"
        :auto-load="canAutoLoadExchangeFeedback"
        :show-load-more="showExchangeFeedbackLoadMore"
        @category-select="handleFeedbackCategorySelect"
        @load-more="handleExchangeFeedbackLoadMore"
      />
    </section>
    <ExchangeDetailFeedbackBulletScreen
      v-if="showFeedbackBulletScreen"
      :items="feedbackBulletItems"
      :bottom-offset="writeFeedbackBottomOffset"
    />
    <button
      v-if="isBackToTopVisible"
      type="button"
      class="exchange-detail-back-to-top cursor-pointer hover:shadow-[0px_0px_24px_0px_rgba(8,19,62,0.04)]"
      data-node-id="13822:29370"
      aria-label="Back to top"
      @click="handleBackToTopClick"
    >
      <span class="flex size-[24px] items-center justify-center" data-node-id="13822:29434">
        <CommonImage :src="backToTopIcon" alt="" class="size-full" width="24" height="24" aria-hidden="true" />
      </span>
    </button>
    <ExchangeDetailExchangeRiskDialog :show="riskDialogVisible" :risk="traderRisk" @close="closeRiskDialog" />
    <ExchangeDetailExchangeInfluenceDialog
      :show="influenceDialogVisible"
      :influence-rating="traderInfluenceRating"
      :market-influence-countries="traderInfluenceCountries"
      @close="closeInfluenceDialog"
    />
    <ExchangeDetailExchangeEmcDialog
      :show="emcDialogVisible"
      @close="closeEmcDialog"
      :trader-data="traderDetailData"
      @feedback="handleEmcFeedback"
    />
    <ExchangeDetailExchangeIapDialog
      :show="iapDialogVisible"
      :trader-data="traderDetailData"
      :guarantee-pop="guaranteePopData"
      @close="iapDialogVisible = false"
      :trader-code="traderCode"
    />
    <ExchangeDetailExchangeFeedbackDialog
      :show="feedbackDialogVisible"
      @close="feedbackDialogVisible = false"
      :allow-exchange-search="false"
      :trader-code="traderCode"
      :exchange-slug="exchangeSlug"
      :feedback-show-type="exchangeFeedbackShowType"
      :feedback-name="exchangeName"
      :feedback-logo="exchangeLogo"
    />
    <ExchangeDetailLicenseDialog
      v-model="licenseDialogVisible"
      :detail="selectedLicenseDetail"
      :loading="licenseDialogLoading"
      :project-code="selectedLicenseSummary?.projectCode || licenseProjectCode"
      :exchange-name="traderBasicInfo?.showName || traderBasicInfo?.localShortName || ''"
    />
  </main>
</template>

<style scoped lang="less">
.exchange-detail-page {
  min-height: 100vh;
  background: #fff;
  color: #292a2e;
}

.exchange-detail-container {
  --exchange-layout-gap: 16px;
  --exchange-main-column: 516fr;
  --exchange-aside-column: 250fr;
  --exchange-regulation-column: 402fr;

  width: min(1200px, calc(100vw - 48px));
  margin: 0 auto;
}

.exchange-detail-main {
  --write-feedback-bottom-safe-area: 120px;

  margin-top: -44px;
  padding-bottom: 80px;
}

.exchange-detail-top-grid {
  display: grid;
  grid-template-columns:
    minmax(0, var(--exchange-main-column)) minmax(0, var(--exchange-aside-column))
    minmax(0, var(--exchange-regulation-column));
  gap: var(--exchange-layout-gap);
  align-items: start;
}

.exchange-detail-column {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 16px;
}

.exchange-funds-section,
.exchange-detail-business-area,
.exchange-detail-background-investigation {
  margin-top: 40px;
}

.exchange-detail-site-visit {
  margin-top: 36px;
}

.exchange-detail-business-area {
  margin-top: 40px;
}

.exchange-detail-deposit-withdrawal {
  margin-top: 40px;
}

.exchange-detail-fee-rate {
  margin-top: 40px;
}

.exchange-detail-feedback {
  margin-top: 40px;
  scroll-margin-top: 150px;
}

.exchange-detail-regulation-disclosure {
  margin-top: 40px;
}

.exchange-detail-overview {
  margin-top: 32px;
}

.exchange-detail-write-feedback {
  position: fixed;
  z-index: 20;
  top: auto;
  inset-inline-end: max(24px, calc((100vw - 1200px) / 2));
  bottom: var(--write-feedback-bottom-safe-area);
}

.exchange-detail-back-to-top {
  position: fixed;
  z-index: 20;
  top: 374px;
  inset-inline-end: 0;
  display: flex;
  width: 56px;
  height: 56px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 16px;
  border: 0.5px solid rgba(11, 18, 40, 0.14);
  border-radius: 1000px 0 0 1000px;
  background: #fff;
  box-shadow: 0 0 24px rgba(8, 19, 62, 0.04);
  border-right: none;
}

@media screen and (max-width: 1199px) {
  .exchange-detail-top-grid {
    display: flex;
    flex-wrap: wrap;
  }

  .exchange-detail-column-main {
    flex: 1 1 516px;
  }

  .exchange-detail-column-aside {
    flex: 0 1 250px;
  }

  .exchange-detail-column-regulation {
    flex: 1 1 402px;
  }
}

@media screen and (max-width: 799px) {
  .exchange-detail-container {
    width: min(1200px, calc(100vw - 32px));
  }

  .exchange-detail-top-grid {
    display: grid;
    grid-template-columns: 1fr;
  }

  .exchange-detail-write-feedback {
    inset-inline-end: 24px;
  }

  .exchange-detail-back-to-top {
    display: none;
  }
}
</style>
